import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import QueryString from 'query-string';
import { useNotify, useService } from '../../../../../hooks';
import FileService, {
  type GetFileResponse,
} from '../../../../../services/FileService';
import {
  useReportErrorToBugsnag,
  useReportInfoToBugsnag,
} from '../../../../../util/bugsnagReporting';
import {
  type KnowledgeBaseCitation,
  isBrowserPreviewableKnowledgeBaseMime,
  isCsvKnowledgeBaseFile,
} from '../../../../../util/knowledgeBaseCitation';

const fileService = new FileService();

const messages = defineMessages({
  knowledgeBaseFileError: {
    id: 'phc-web-toolkit.subjectViewer.attachments.knowledgeBaseCitationInline.error',
    defaultMessage:
      'Could not open this knowledge base file. Try again or download from Files.',
  },
});

export type OpenCsvKnowledgeBaseFile = {
  /** Pre-signed open URL (no Content-Disposition) for fetching CSV bytes. */
  url: string;
  /**
   * Pre-signed download URL with Content-Disposition: attachment, for the
   * "Download" affordance shown alongside the in-app CSV preview.
   */
  downloadUrl: string;
  fileName: string;
  contentType?: string;
};

export type UseOpenKnowledgeBaseFileOptions = {
  /**
   * Invoked instead of `window.open` when the resolved file is a CSV. When
   * omitted, CSVs fall back to the legacy "open in new tab" behavior — same
   * as every other previewable mime — so existing callers keep working until
   * they opt in.
   */
  onOpenCsv?: (file: OpenCsvKnowledgeBaseFile) => void;
};

/**
 * Resolves a knowledge-base S3 citation to a file-service file and opens it in a
 * new tab. Mirrors the qa-agent chat citation handling so inline citations and
 * attachment-list entries share the same "link to the file-service file" behavior.
 *
 * The browser resolves the file id via a name+datasetId lookup at click time —
 * nothing about a file UUID is encoded in the citation — then falls back to a
 * signed download link for mimes the browser cannot preview.
 *
 * When the resolved file is a CSV and `options.onOpenCsv` is provided, the hook
 * hands off the resolved URLs to the caller instead of `window.open`-ing the
 * file. That lets callers render the CSV in-app as a table rather than letting
 * the browser save it to disk.
 */
export const useOpenKnowledgeBaseFile = (
  citation: KnowledgeBaseCitation,
  options: UseOpenKnowledgeBaseFileOptions = {},
) => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const reportErrorToBugsnag = useReportErrorToBugsnag();
  const reportInfoToBugsnag = useReportInfoToBugsnag();

  const [fetchFiles] = useService(fileService.fetchFiles);
  const [fetchOpenLink] = useService(fileService.fetchOpenLink);
  const [fetchDownloadLink] = useService(fileService.fetchDownloadLink);

  const { onOpenCsv } = options;

  return React.useCallback(async () => {
    try {
      const list = await fetchFiles({
        datasetId: citation.projectId,
        name: citation.name,
        pageSize: 1,
      });
      const file = list.data?.items?.find(
        (item: GetFileResponse) => item.name === citation.name,
      );
      if (!file?.id) {
        notify(formatMessage(messages.knowledgeBaseFileError), 'error');
        return;
      }
      const res = await fetchOpenLink(file.id);
      const downloadUrl = res.data?.downloadUrl;
      if (!downloadUrl) {
        notify(formatMessage(messages.knowledgeBaseFileError), 'error');
        return;
      }
      const effectiveMime = res.data?.contentType || file.contentType;

      // CSV gets routed to the in-app viewer (when the caller registered one)
      // because browsers don't natively render text/csv — they download it and
      // leave the user to find a spreadsheet tool.
      if (
        onOpenCsv &&
        isCsvKnowledgeBaseFile({ mime: effectiveMime, name: citation.name })
      ) {
        // Best-effort fetch of the attachment-disposition URL so the modal's
        // "Download" button gets a true download link instead of inheriting the
        // open-link semantics. Failure here is non-fatal: we can still preview.
        let dlUrl: string | undefined;
        try {
          const dl = await fetchDownloadLink(file.id);
          dlUrl = dl.data?.downloadUrl;
        } catch (e) {
          // Swallow: caller can still hit the open URL to grab the bytes.
        }
        onOpenCsv({
          url: downloadUrl,
          downloadUrl: dlUrl ?? downloadUrl,
          fileName: citation.displayName,
          contentType: effectiveMime,
        });
        return;
      }

      if (!isBrowserPreviewableKnowledgeBaseMime(effectiveMime)) {
        reportInfoToBugsnag(
          'Knowledge base citation mime not shown in browser; download fallback',
          {
            metaData: {
              knowledgeBase: {
                mime: effectiveMime,
                projectId: citation.projectId,
                name: citation.name,
              },
            },
          },
        );
        const dl = await fetchDownloadLink(file.id);
        const dlUrl = dl.data?.downloadUrl;
        if (dlUrl) {
          window.open(dlUrl);
        } else {
          notify(formatMessage(messages.knowledgeBaseFileError), 'error');
        }
        return;
      }
      const mime = (effectiveMime || '').toLowerCase();
      let url = downloadUrl;
      if (mime === 'application/pdf' && citation.page) {
        url = `${downloadUrl}#${QueryString.stringify({
          page: citation.page,
        })}`;
      }
      window.open(url);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      reportErrorToBugsnag(err, {
        metaData: {
          knowledgeBase: { projectId: citation.projectId, name: citation.name },
        },
      });
      notify(formatMessage(messages.knowledgeBaseFileError), 'error');
    }
  }, [
    citation.displayName,
    citation.name,
    citation.page,
    citation.projectId,
    fetchDownloadLink,
    fetchFiles,
    fetchOpenLink,
    formatMessage,
    notify,
    onOpenCsv,
    reportErrorToBugsnag,
    reportInfoToBugsnag,
  ]);
};
